import type { ChartDataSymbol } from '@/features/charts/types/chartData';

export const NO_DATA_ERROR = 'No data found. Try another date.';
export const INVALID_PERIODS_ERROR = 'Data not available.';

export type ValidationResult = {
  invalidSymbols: ChartDataSymbol[];
  errorType: 'no-data' | 'invalid-periods' | null;
};

/**
 * Validates curve data symbols and returns validation result
 * @param symbols - Array of chart data symbols to validate
 * @returns Object containing invalid symbols and error type (if any)
 */
export const validateCurveData = (symbols: ChartDataSymbol[]): ValidationResult => {
  const dataSymbols = symbols.filter(symbol => checkIfNoDataFor(symbol));
  const matchingSymbols = filterMatchingPeriods(dataSymbols);
  const invalidSymbols = symbols.filter(symbol => !matchingSymbols.includes(symbol));

  let errorType: 'no-data' | 'invalid-periods' | null = null;

  if (dataSymbols.length !== symbols.length) {
    errorType = 'no-data';
  } else if (matchingSymbols.length !== symbols.length) {
    errorType = 'invalid-periods';
  }

  return { invalidSymbols, errorType };
};
const checkIfNoDataFor = (symbol: ChartDataSymbol) => {
  return !(
    symbol.transactionDate &&
    symbol.series instanceof Object &&
    Object.keys(symbol.series).length === 0
  );
};
export const filterMatchingPeriods = (symbols: ChartDataSymbol[]): ChartDataSymbol[] => {
  return symbols.reduce((prev: ChartDataSymbol[], curr: ChartDataSymbol): ChartDataSymbol[] => {
    if (curr?.transactionDate === undefined) {
      return [...prev, curr];
    }

    const currSeriesData =
      curr.series?.Mid ?? curr.series?.Index ?? curr.series?.Low ?? curr.series?.High;

    const currKey = JSON.stringify(curr.key);
    const matching = prev.find((symbol: ChartDataSymbol) => {
      if (JSON.stringify(symbol.key) !== currKey) return symbol;

      const seriesData =
        symbol.series?.Mid ?? symbol.series?.Index ?? symbol.series?.Low ?? symbol.series?.High;
      return Math.abs(seriesData.length - currSeriesData.length) <= 2;
    });
    if (matching) {
      return [...prev, curr];
    }
    return prev;
  }, [] as ChartDataSymbol[]);
};
